import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";

// Lazy load non-critical routes to reduce initial bundle size
const NotFound = lazy(() => import("./pages/NotFound"));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesAndPricingPage = lazy(() => import("./pages/ServicesAndPricingPage"));
const SchedulePage = lazy(() => import("./pages/SchedulePage"));
const TransparentPricingPage = lazy(() => import("./pages/TransparentPricingPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/quote" element={<Navigate to="/services-pricing#get-quote" replace />} />
              <Route path="/how-it-works-web-design-process" element={<HowItWorksPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/services-pricing" element={<ServicesAndPricingPage />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/transparent-pricing" element={<TransparentPricingPage />} />
              {/* Redirects for old routes */}
              <Route path="/services" element={<Navigate to="/services-pricing" replace />} />
              <Route path="/pricing" element={<Navigate to="/services-pricing" replace />} />
              <Route path="/faq" element={<Navigate to="/how-it-works-web-design-process" replace />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<AdminDashboardPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
